/**
 * Tag — primitive DOM N1 (ESM, ha/440).
 *
 * Conversion namespace→ESM. Recette appliquée :
 *  1. `namespace HA.Core.HTML { … }` → exports ESM top-level.
 *  2. `import Y = HA.Core.X` → `import { Y } from "…"` ESM.
 *  3. Refs implicites (`toMap`, `log2`, `timeout`) → imports explicites depuis `HA`.
 *
 * Dépendances out-of-cluster CONSERVÉES comme stubs locaux (ha/441 :
 * remplacement par les vrais imports ESM une fois leurs modules portés) :
 *  - `Span`, `P` (HA.Core.HTML.Span/P) : utilisés dans `createByTypeAndAddAll`
 *    pour wrapper le texte inline. Le test ha/440 (Entity.App vide) ne traverse
 *    pas cette branche → stubs vides suffisent à la compilation.
 *  - `HtmlService.createByType` (HA.Core.HTML.Service) : dispatcher de type N3.
 *    Même argument : non exercé par le test du registre.
 */

import { HAObject, registerTagCtor } from "../HAObject";
import { toMap, log2 } from "../../../HA";
import * as Presentation from "./TagPresentation";
import * as Behavior from "./TagBehavior";

// ─── Slots anti-TDZ (ha/442) ─────────────────────────────────────────────────
// Span/P étendent Tag → import direct créerait un cycle TDZ. Pattern slot :
// les modules Span.ts et P.ts appellent registerSpanCtor/registerPCtor après
// leur définition de classe ; HtmlService.ts appelle registerCreateByType.
let _SpanCtor: new (data?: any) => any = class { constructor(_d?: any) {} };
let _PCtor: new (data?: any) => any   = class { constructor(_d?: any) {} };
let _createByTypeFn: ((d: Map<any, any>, m: Tag, t?: any) => void) | null = null;

export function registerSpanCtor(ctor: new (data?: any) => any): void { _SpanCtor = ctor; }
export function registerPCtor(ctor: new (data?: any) => any): void { _PCtor = ctor; }
export function registerCreateByType(fn: (d: Map<any, any>, m: Tag, t?: any) => void): void {
    _createByTypeFn = fn;
}

export class Tag extends HAObject {

    protected static createElement(name: string): HTMLElement {
        return document.createElement(name);
    }

    public addAttribute(name: string, value: string): void {
        this.setAttribute(name, this.translate(value));
    }

    public addAttributes(attributes: Array<Map<string, any> | { n: string; v: any }>): void {
        attributes.forEach((a) => {
            if (a instanceof Map) {
                this.addAttribute(a.get("n"), a.get("v"));
            } else {
                this.addAttribute(a.n, a.v);
            }
        });
    }

    public equals(value: any): boolean {
        return this === value;
    }

    public getChildren(): Tag[] {
        const els = this.getElement().childNodes as NodeListOf<HTMLElement>;
        const result: Tag[] = [];
        for (const el of Array.from(els)) {
            result.push(new Tag(el));
        }
        return result;
    }

    public fixHeight(): void {
        Presentation.fixHeight(this.getElement());
    }

    public focus(): void {
        Presentation.focus(this.getElement());
    }

    public insertOnTop(tag: Tag): void {
        const el: Element = this.getElement();
        el.insertBefore(tag.getElement(), el.firstChild);
    }

    public show(): void {
        Presentation.show(this.getElement(), this.get("visibility"));
    }

    public hide(defaut?: any): void {
        this.addToData("visibility", this.getVisibility(defaut));
        Presentation.hide(this.getElement());
    }

    public countChildren(): number {
        return this.getElement().childNodes.length;
    }

    public clear(): this {
        const element: Element = this.getElement();
        if (element) {
            element.innerHTML = "";
        }
        return this;
    }

    public getContent(): string {
        return this.getElement().innerHTML;
    }

    public getStripText(): string {
        const text = this.getText();
        return text.replace(/<[^>]*>/g, "");
    }

    public getText(): string {
        const t = this.getElement().textContent || this.getElement().innerText;
        return t;
    }

    public addClasses(names: any): void {
        if (!names) return;
        if (typeof names === "string") {
            names.split(/\s+/).forEach((c: string) => { this.addClass(c); });
            return;
        }
        names.forEach((c: string) => { this.addClass(c); });
    }

    public addClass(name: any): void {
        if (!name) return;
        if (name && typeof name === "string" && name.startsWith("#")) {
            name = this.translate(name);
        }
        if (typeof name === "string") {
            name.split(/\s+/).forEach((n: string) => {
                if (n) this.getElement().classList.add(n);
            });
            return;
        }
        this.getElement().classList.add(name);
    }

    public removeClasses(names: string[]): void {
        names.forEach((c: string) => { this.removeClass(c); });
    }

    public removeClass(name: string): void {
        this.getElement().classList.remove(name);
    }

    public onClick(func: any, blockClickDelay = 1000): void {
        Behavior.onClick(this.getElement(), func, blockClickDelay);
    }

    public onChange(func: any): void {
        Behavior.onChange(this.getElement(), func);
    }

    public setAttribute(name: string, value: any): void {
        this.getElement().setAttribute(name, value);
    }

    public getAttributes(names: string[], skipNull?: boolean): Map<string, string | null> {
        const element: Element = this.getElement();
        const result = new Map<string, string | null>();
        if (element) {
            names.forEach((name) => {
                const v = element.getAttribute(name);
                if (!skipNull || v) result.set(name, v);
            });
        }
        return result;
    }

    public getId(): any {
        return this.get("id");
    }

    public setId(id: string | number): void {
        this.addToData("id", id);
        this.getElement().setAttribute("id", String(id));
    }

    public getValue(): any {
        return (this.getElement() as HTMLInputElement).value;
    }

    public querySelector(filter: string): Element | null {
        return this.getElement().querySelector(filter);
    }

    public getElementId(): string | null {
        return this.getAttribute("id");
    }

    public getAttribute(name: string): string | null {
        return this.getElement().getAttribute(name);
    }

    public setText(text: string | number): void {
        const element = this.getElement();
        // textarea : le setter innerText convertit chaque \n en <br>, invalides
        // dans un textarea → les sauts de ligne du contenu (blocs HADown des
        // articles : ##youtube\n<id>…) étaient PERDUS et un save corrompait le
        // bloc. La valeur d'un textarea se pose via `value`, fidèle au source.
        if (element instanceof HTMLTextAreaElement) {
            element.value = String(text);
            return;
        }
        // innerText n'existe pas dans les moteurs sans rendu (jsdom → propriété
        // undefined, texte silencieusement perdu — même famille que le setText
        // SVG, HA-68). Navigateur : comportement inchangé.
        if (typeof (element as HTMLElement).innerText === "string") {
            (element as HTMLElement).innerText = String(text);
        } else {
            element.textContent = String(text);
        }
    }

    public appendChild(element: any): void {
        if (element instanceof HAObject) {
            element = element.getElement();
        }
        this.getElement().appendChild(element);
    }

    public addFistChild(element: any): void {
        if (element instanceof HAObject) {
            element = element.getElement();
        }
        this.getElement().insertAdjacentElement("afterbegin", element);
    }

    public removeById(id: string): void {
        const el = this.findElementById(id);
        if (el) this.getElement().removeChild(el);
    }

    public findByAttribute(attributeName: string): Tag | null {
        const element = this.findElementByAttribute(attributeName);
        return element ? new Tag(element) : null;
    }

    public findElementByAttribute(attributeName: string): HTMLElement {
        const element = this.getElement() || this.getParentElement() || document;
        return element.querySelector("[" + attributeName + "]") as HTMLElement;
    }

    public findElementByQuery(name: string): HTMLElement {
        const element = this.getElement() || this.getParentElement() || document;
        return element.querySelector(name) as HTMLElement;
    }

    public findByQuery(name: string): Tag {
        const element = this.getElement() || this.getParentElement() || document;
        return new Tag(element.querySelector(name) as HTMLElement);
    }

    public findById(id: string, useDoc = false): Tag | null {
        const element = this.findElementById(id, useDoc);
        return element ? new Tag(element) : null;
    }

    public findElementById(id: string, useDoc = false): HTMLElement | null {
        try {
            const element = useDoc ? document : (this.getElement() || this.getParentElement() || document);
            return element.querySelector("#" + id) as HTMLElement;
        } catch {
            return null;
        }
    }

    public findByTag(tagName: string): HTMLElement {
        const element = this.getElement() || this.getParentElement() || document;
        return element.querySelector(tagName) as HTMLElement;
    }

    public fetchByTagName(tagName: string): Tag[] {
        const element = this.getElement() || this.getParentElement() || document;
        const els = element.querySelectorAll(tagName) as NodeListOf<HTMLElement>;
        const result: Tag[] = [];
        for (const el of Array.from(els)) result.push(new Tag(el));
        return result;
    }

    public fetchByAttribute(attributeName: string): Tag[] {
        const element = this.getElement() || this.getParentElement() || document;
        const els = element.querySelectorAll("[" + attributeName + "]") as NodeListOf<HTMLElement>;
        const result: Tag[] = [];
        for (const el of Array.from(els)) result.push(new Tag(el));
        return result;
    }

    public invisible(): void {
        Presentation.invisible(this.getElement());
    }

    public getTagName(): string {
        return this.get("_tn") || "div";
    }

    public addListener(name: string, func: any): void {
        Behavior.addListener(this.getElement(), name, func);
    }

    public translate(word: string | any, translation?: any): any {
        if (!word || typeof word !== "string") return word;
        if (word.startsWith("##")) {
            translation = translation || toMap(this.get("trans"));
            const v = translation.get(word.slice(2));
            return v !== undefined ? v : null;
        }
        if (word.startsWith("#")) {
            translation = translation || toMap(this.get("trans"));
            const v = translation.get(word.slice(1));
            if (v !== undefined) return v;
        }
        return word;
    }

    public scrollIntoView(): void {
        Presentation.scrollIntoView(this.getElement());
    }

    protected hasTrans(word: string | any, translation?: any): boolean {
        if (!word || typeof word !== "string") return false;
        translation = translation || toMap(this.get("trans"));
        if (word.startsWith("##")) return translation.get(word.slice(2)) !== undefined;
        if (word.startsWith("#")) return translation.get(word.slice(1)) !== undefined;
        return false;
    }

    protected createByType(data: Map<any, any>, main: Tag): void {
        if (_createByTypeFn) { _createByTypeFn(data, main, toMap(this.get("trans"))); }
    }

    protected translateAll(list: Map<any, any>): Map<any, any> {
        const rep: Map<any, any> = new Map();
        list.forEach((v, k) => {
            rep.set(k, this.translate(v));
        });
        return rep;
    }

    protected trans(id: any): any {
        return this.translate(this.getData().get(id));
    }

    protected createByTypeAndAddAll(children: any[] | string | Map<any, any>, tag: Tag): void {
        if (typeof children === "string" || typeof children === "number") {
            const tchildren = this.translate(children);
            if (this instanceof _SpanCtor || this instanceof _PCtor) {
                this.setText(tchildren);
            } else {
                this.appendChild(new _SpanCtor(tchildren));
            }
            return;
        }
        if ((children && Array.isArray(children)) || children instanceof Map) {
            const trans = this.get("trans");
            try {
                Array.from(children as Iterable<any>).forEach((item: any) => {
                    if (typeof item === "string" || typeof item === "number") {
                        const titem = this.translate(item);
                        if (Array.isArray(titem)) {
                            this.createByTypeAndAddAll(titem, tag);
                            return;
                        }
                        if (titem !== null && titem !== undefined && typeof titem === "object") {
                            const nitem = toMap(titem);
                            if (nitem.size > 0) {
                                if (trans) nitem.set("trans", trans);
                                this.createByType(nitem as Map<any, any>, tag);
                            }
                            return;
                        }
                        if (this instanceof _SpanCtor) {
                            this.setText(titem);
                        } else {
                            this.appendChild(new _SpanCtor(titem));
                        }
                        return;
                    }
                    if (Array.isArray(item)) {
                        this.createByTypeAndAddAll(item, tag);
                        return;
                    }
                    const nitem = toMap(item);
                    if (nitem.size === 0) return;
                    if (trans) nitem.set("trans", trans);
                    this.createByType(nitem as Map<any, any>, tag);
                });
            } catch (e) {
                log2(e);
                throw e;
            }
        }
    }

    protected getVisibility(defaut?: any): any {
        return Presentation.getVisibility(this.getElement(), defaut);
    }

    protected searchAndAddElement(): void {
        let param: any;
        let element: Element | null = null;
        param = this.get("id");
        if (param) {
            element = this.findElementById(param);
        }
        if (!element) {
            param = this.get("attribute");
            if (param) {
                element = this.findElementByAttribute(param);
            }
            if (!element) {
                param = this.get("html");
                if (param) {
                    const el = document.createElement("div");
                    el.innerHTML = this.translate(param);
                    element = el.firstChild as Element;
                }
                if (!element) {
                    param = this.get("tagName");
                    if (param) {
                        element = this.findByTag(param);
                    } else {
                        this.createSelf();
                        return;
                    }
                }
            }
        }
        this.setElement(element);
    }

    protected setElement(element?: Element | null): Map<string, any> {
        return this.addToData("element", element);
    }

    protected getParentElement(): any {
        return this.get("pelement");
    }

    protected initialize(data: Map<any, any> | Tag | Element | HAObject | any): void {
        if ((data instanceof Object)
            && !(data instanceof Map)
            && !(data instanceof Element)
            && !(data instanceof HAObject)) {
            data = toMap(data);
        }

        if (data instanceof Map && data.has("tag")) {
            const t = data.get("tag");
            if (t instanceof Tag) {
                data.set("element", t.getElement());
            }
        } else if (data instanceof Element) {
            data = toMap({ element: data });
        } else if (data instanceof HAObject) {
            data = toMap({ element: data.getElement() });
        }

        super.initialize(data);
        if (!this.getElement()) {
            this.searchAndAddElement();
        }

        if (typeof data === "string") {
            this.setText(this.translate(data));
        }

        if (this.has("txt")) {
            this.setText(this.translate(this.get("txt")));
        }
        if (this.has("onClick")) {
            this.onClick(this.get("onClick"));
        } else if (this.has("click")) {
            this.onClick(this.get("click"));
        }
        if (this.has("onChange")) {
            this.onChange(this.get("onChange"));
        }
        if (this.has("className")) {
            this.addClass(this.get("className"));
        }
        if (this.has("classes")) {
            this.addClasses(this.get("classes"));
        }
        if (this.has("class")) {
            const _class = this.get("class");
            if (typeof _class === "string") this.addClass(_class);
            else this.addClasses(_class);
        }
        if (this.has("attributes")) {
            this.addAttributes(this.get("attributes"));
        }
        if (this.has("-events")) {
            this.initEvents(this.get("-events"));
        }
        if (this.has("eid")) {
            this.setId(this.translate(this.get("eid")));
        }
    }

    protected initEvents(events: Map<string, () => void>): void {
        Behavior.initEvents(this.getElement(), events);
    }

    protected createSelf(): void {
        if (!this.get("element")) {
            this.setElement(Tag.createElement(this.getTagName()));
        }
        if (this.has("c")) {
            this.createByTypeAndAddAll(this.get("c"), this);
        }
    }

    protected replaceValues(data: any): any {
        if (data === null || data === undefined || typeof data === "number" || typeof data === "boolean") {
            return data;
        }
        // Chaîne : résout les références trans (#name / ##name), sinon inchangée.
        if (typeof data === "string") {
            return /#.+/.test(data) ? this.translate(data) : data;
        }
        // Tableau : résout chaque élément en place (récursif).
        if (Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                data[i] = this.replaceValues(data[i]);
            }
            return data;
        }
        // Map : résout chaque valeur en place. NB : itérer les VALEURS, pas
        // `Array.from(map)` (qui rend les entrées [clé,valeur] → récursion
        // infinie, régression de conversion ha/441).
        if (data instanceof Map) {
            if (data.has("norepl")) return data;
            data.forEach((v: any, k: any) => { data.set(k, this.replaceValues(v)); });
            return data;
        }
        // Objet simple : résout chaque propriété en place.
        if (typeof data === "object") {
            if ((data as any).norepl !== undefined) return data;
            for (const k of Object.keys(data)) {
                data[k] = this.replaceValues(data[k]);
            }
            return data;
        }
        return data;
    }
}

// Enregistre Tag auprès de HAObject pour que `getElement()` reproduise l'`instanceof
// HA.Core.HTML.Tag` du legacy sans cycle d'import (side-effect top-level).
registerTagCtor(Tag);
